import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppBadge, AppButton } from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import { FieldWorkspace } from "../../../shared/components/FieldWorkspace";
import { LifecycleStrip } from "../../../shared/components/LifecycleStrip";
import { getUser } from "../../auth/session";
import {
  IconCheck,
  IconChevronRight,
  IconError,
  IconHourglass,
  IconInfo,
  IconSpinner,
  IconWarning,
} from "../../../shared/components/icons";
import { factsFromCustomer, resolveJourney, type JourneyFacts } from "../../field/journey";
import { withEntityQuery } from "../../../shared/navigation/entityPrefill";
import { listInstallations } from "../api";
import { customerDisplayName, useCustomersMap, useProductsMap } from "../enrichment";
import { formatHHMM, readTaskCache, writeTaskCache, type TaskCacheRow } from "../offline";
import type { Installation, InstallationStatus } from "../types";

/**
 * S1 — My installations (handoff TASK-008, s1-my-installations-*).
 * Technician task list: customer / ward / product / status badge.
 * pending_approval is AMBER (waiting on the office, not the technician).
 * Offline: last-synced list from the device cache with an "as of HH:MM" note.
 * Installation rows carry IDs only — customer/ward/product are hydrated via
 * the enrichment hooks (best-effort, fall back to IDs).
 */

/** Statuses a technician still acts on or tracks (cancelled/completed history hidden). */
const VISIBLE: InstallationStatus[] = [
  "scheduled",
  "in_progress",
  "correction_requested",
  "pending_approval",
];

function statusBadge(status: InstallationStatus) {
  switch (status) {
    case "scheduled":
      return (
        <AppBadge tone="info">
          <IconInfo size={12} /> Scheduled
        </AppBadge>
      );
    case "in_progress":
      return (
        <AppBadge tone="warning">
          <IconHourglass size={12} /> In progress
        </AppBadge>
      );
    case "pending_approval":
      // AMBER per handoff — office gate, not a technician error.
      return (
        <AppBadge tone="warning">
          <IconHourglass size={12} /> Pending approval
        </AppBadge>
      );
    case "correction_requested":
      return (
        <AppBadge tone="error">
          <IconWarning size={12} /> Correction requested
        </AppBadge>
      );
    case "approved":
      return (
        <AppBadge tone="success">
          <IconCheck size={12} /> Approved
        </AppBadge>
      );
    default:
      return <AppBadge tone="neutral">{status}</AppBadge>;
  }
}

function formatWhen(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}, ${formatHHMM(d.toISOString())}`;
}

export function S1MyInstallations() {
  useFieldHeader("Installation Workspace", "Assigned tasks, approval readiness, and next actions");
  useFieldJourney({ flow: "install", step: 0, hideBack: true });
  const navigate = useNavigate();
  const canSchedule = ["business_admin", "commercial_officer"].includes(
    getUser()?.role || "",
  );

  const query = useQuery({
    queryKey: ["inst-tasks"],
    queryFn: () => listInstallations({ assignedToMe: true, perPage: 100 }),
    retry: 1,
    staleTime: 15_000,
  });

  const rows = useMemo(
    () => (query.data?.installations ?? []).filter((i) => VISIBLE.includes(i.status)),
    [query.data],
  );

  const customers = useCustomersMap(rows.map((r) => r.customer_id));
  const products = useProductsMap(rows.map((r) => r.product_id));
  const [focusId, setFocusId] = useState<number | null>(null);

  // Snapshot the enriched list for the offline variant.
  useEffect(() => {
    if (!query.data) return;
    const cacheRows: TaskCacheRow[] = rows.map((r) => {
      const c = customers.get(r.customer_id);
      return {
        id: r.id,
        customer_id: r.customer_id,
        product_id: r.product_id,
        contract_id: r.contract_id,
        status: r.status,
        scheduled_date: r.scheduled_date,
        created_at: r.created_at,
        customer_name: c ? customerDisplayName(c, r.customer_id) : undefined,
        ward: c?.ward ?? undefined,
        product_name: r.product_id ? products.get(r.product_id) : undefined,
        contract_number:
          c?.active_contract && c.active_contract.id === r.contract_id
            ? c.active_contract.contract_number
            : undefined,
      };
    });
    writeTaskCache(cacheRows);
  }, [query.data, rows, customers, products]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (query.isPending) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Loading tasks…</h2>
      </div>
    );
  }

  // ── Error / offline → cached variant ("as of HH:MM") ───────────────────────
  if (query.isError) {
    const cache = readTaskCache();
    if (cache && cache.rows.length > 0) {
      return (
        <>
          <div className="pg-offline-banner" role="status">
            <IconWarning size={14} /> Offline — cached as of {formatHHMM(cache.at)}
          </div>
          {cache.rows.map((r) => (
            <Link key={r.id} className="pg-lead-row" to={`/field/installations/${r.id}/scan`}>
              <span>
                <div className="lead-name">{r.customer_name ?? `Customer #${r.customer_id}`}</div>
                <div className="lead-meta">
                  {[r.ward, r.product_name, r.contract_number, formatWhen(r.scheduled_date ?? r.created_at)]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </span>
              <span className="lead-tail">
                {statusBadge(r.status)}
                <IconChevronRight size={14} />
              </span>
            </Link>
          ))}
        </>
      );
    }
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Could not load tasks</h2>
        <p>{query.error instanceof Error ? query.error.message : "Something went wrong"}</p>
        <AppButton variant="secondary" type="button" onClick={() => void query.refetch()}>
          Retry
        </AppButton>
      </div>
    );
  }

  // ── Empty ──────────────────────────────────────────────────────────────────
  if (rows.length === 0) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconInfo size={40} />
        </span>
        <h2>No tasks assigned</h2>
        <p>New installation tasks assigned to you will appear here.</p>
      </div>
    );
  }

  // ── Default list ───────────────────────────────────────────────────────────
  const selected = rows.find((r) => r.id === focusId) ?? rows[0];
  const selectedCustomer = selected ? customers.get(selected.customer_id) : undefined;

  const journeyFacts: JourneyFacts = selectedCustomer
    ? {
        ...factsFromCustomer({
          id: selected.customer_id,
          active_contract: selectedCustomer.active_contract
            ? {
                id: selectedCustomer.active_contract.id,
                amount_paid: selectedCustomer.active_contract.amount_paid,
              }
            : null,
          devices: selected.device_id ? [{ id: selected.device_id }] : [],
        }),
        installationId: selected.id,
        installSubmittedOrApproved:
          selected.status === "pending_approval" ||
          selected.status === "approved",
        firstTokenIssued: Boolean(selected.first_token_issued_at),
        hasDeviceAssigned: Boolean(selected.device_id || selected.serial_confirmed),
      }
    : {
        customerId: selected.customer_id,
        installationId: selected.id,
        leadCaptured: true,
        kycApproved: true,
        hasActiveContract: Boolean(selected.contract_id),
        hasDeviceAssigned: Boolean(selected.device_id),
        installSubmittedOrApproved:
          selected.status === "pending_approval" || selected.status === "approved",
      };

  const journey = resolveJourney(journeyFacts);

  return (
    <FieldWorkspace
      left={
        <>
          <div style={{ marginBottom: "var(--pg-s3)" }}>
            <h3 style={{ margin: 0 }}>Installation queue</h3>
            <p style={{ margin: "6px 0 0", color: "var(--pg-text-muted)", fontSize: "var(--pg-fs-meta)" }}>
              Track assigned installations and progress each job through field checkpoints.
            </p>
          </div>
          {rows.map((r: Installation) => {
            const c = customers.get(r.customer_id);
            const meta = [
              c?.ward,
              r.product_id ? products.get(r.product_id) : undefined,
              c?.active_contract && c.active_contract.id === r.contract_id
                ? c.active_contract.contract_number
                : undefined,
              formatWhen(r.scheduled_date ?? r.created_at),
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <button
                key={r.id}
                type="button"
                className="pg-lead-row"
                onClick={() => setFocusId(r.id)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  marginBottom: 8,
                  outline: selected?.id === r.id ? "2px solid var(--pg-green)" : "none",
                }}
              >
                <span>
                  <div className="lead-name">{customerDisplayName(c, r.customer_id)}</div>
                  <div className="lead-meta">{meta || "—"}</div>
                </span>
                <span className="lead-tail">
                  {statusBadge(r.status)}
                  <IconChevronRight size={14} />
                </span>
              </button>
            );
          })}
        </>
      }
      right={
        <>
          <LifecycleStrip journey={journey} title="Customer journey" />
          {selected ? (
            <div className="pg-summary">
              <div className="row">
                <span className="k">Current focus</span>
                <span className="v">{customerDisplayName(selectedCustomer, selected.customer_id)}</span>
              </div>
              <div className="row">
                <span className="k">Local steps</span>
                <span className="v">Queue → Scan → Evidence → Payment → Submit</span>
              </div>
              <div className="row">
                <span className="k">Status</span>
                <span className="v">{selected.status.replace("_", " ")}</span>
              </div>
              <div className="row">
                <span className="k">Scheduled</span>
                <span className="v">{formatWhen(selected.scheduled_date ?? selected.created_at) ?? "—"}</span>
              </div>
              <div className="row">
                <span className="k">Next owner</span>
                <span className="v">
                  {selected.status === "pending_approval" ? "Office approver" : "Field technician"}
                </span>
              </div>
            </div>
          ) : null}
          <div className="pg-actions" style={{ position: "static", paddingBottom: 0 }}>
            {selected ? (
              <AppButton
                type="button"
                onClick={() => navigate(`/field/installations/${selected.id}/scan`)}
              >
                Continue selected install
              </AppButton>
            ) : null}
            {canSchedule ? (
              <AppButton
                variant="secondary"
                type="button"
                onClick={() => navigate(
                  selected
                    ? withEntityQuery("/installations/new", { customer_id: selected.customer_id })
                    : "/installations/new",
                )}
              >
                Schedule installation
              </AppButton>
            ) : null}
            {selectedCustomer?.active_contract ? null : selected ? (
              <AppButton
                variant="secondary"
                type="button"
                onClick={() => navigate(`/customers/${selected.customer_id}?contract=activate`)}
              >
                Go to Contract
              </AppButton>
            ) : null}
            {selected && !selected.device_id ? (
              <AppButton
                variant="secondary"
                type="button"
                onClick={() => navigate(withEntityQuery("/devices", {
                  assignment: "unassigned",
                  customer_id: selected.customer_id,
                }))}
              >
                Assign device
              </AppButton>
            ) : null}
          </div>
        </>
      }
    />
  );
}

